import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface BeforeAndAfterQuestionItemState {
  // sampleId -> currently selected optionIds (a set; single-select samples hold
  // 0 or 1 entries, multi-select samples hold 0..n).
  selections: Record<string, number[]>;
  // sampleId -> list of optionIds that have been submitted
  submittedSelections: Record<string, number[]>;
  isSubmitted: boolean;
}

export type BeforeAndAfterQuestionSliceState = Record<
  string,
  BeforeAndAfterQuestionItemState
>;

const getDefaultState = (): BeforeAndAfterQuestionItemState => ({
  selections: {},
  submittedSelections: {},
  isSubmitted: false,
});

const beforeAndAfterQuestionSlice = createSlice({
  name: "beforeAndAfter",
  initialState: {} as BeforeAndAfterQuestionSliceState,
  reducers: {
    selectOption: (
      state,
      action: PayloadAction<{
        componentID: string;
        sampleId: string;
        optionId: number;
        multiSelect: boolean;
      }>
    ) => {
      const { componentID, sampleId, optionId, multiSelect } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      const current = state[componentID].selections[sampleId] ?? [];
      if (multiSelect) {
        // Toggle the option within the sample's selected set.
        state[componentID].selections[sampleId] = current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId];
      } else {
        // Single-select: the set always holds exactly the picked option.
        state[componentID].selections[sampleId] = [optionId];
      }
    },
    submit: (
      state,
      action: PayloadAction<{
        componentID: string;
        multiSelectSampleIds: string[];
      }>
    ) => {
      const { componentID, multiSelectSampleIds } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      const item = state[componentID];
      Object.entries(item.selections).forEach(([sampleId, optionIds]) => {
        if (multiSelectSampleIds.includes(sampleId)) {
          // Multi-select: record the exact submitted set so grading can compare
          // it by set equality and per-option feedback reflects this attempt.
          item.submittedSelections[sampleId] = [...optionIds];
        } else {
          // Single-select: accumulate submitted options so previously-submitted
          // (and locked) picks keep their success/fail state across attempts.
          if (!item.submittedSelections[sampleId]) {
            item.submittedSelections[sampleId] = [];
          }
          optionIds.forEach((optionId) => {
            if (!item.submittedSelections[sampleId].includes(optionId)) {
              item.submittedSelections[sampleId].push(optionId);
            }
          });
        }
      });
      item.isSubmitted = true;
    },
    reset: (state, action: PayloadAction<string>) => {
      state[action.payload] = getDefaultState();
    },
    // Soft-reset a single sample: clear its current and submitted selections so
    // the learner can pick again after an incorrect multi-select submission
    // (mirrors the "Try again" behaviour of the multi-select question). The
    // attempt counter, other samples, and the overall submitted flag are left
    // untouched.
    tryAgainSample: (
      state,
      action: PayloadAction<{ componentID: string; sampleId: string }>
    ) => {
      const { componentID, sampleId } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      delete state[componentID].selections[sampleId];
      delete state[componentID].submittedSelections[sampleId];
    },
  },
});

export const beforeAndAfterQuestionActions =
  beforeAndAfterQuestionSlice.actions;
export const beforeAndAfterQuestionReducer =
  beforeAndAfterQuestionSlice.reducer;

export const selectBeforeAndAfterQuestionState = (
  state: { beforeAndAfter: BeforeAndAfterQuestionSliceState },
  componentId: string
) => state.beforeAndAfter?.[componentId] || getDefaultState();
